/**
 * AuthContext.tsx — holds the current session user (or null) and exposes
 * login/logout. On mount it calls GET /auth/get-session once to restore a
 * session from the Better Auth cookie (if the browser already has one).
 *
 * Also fetches the signed-in user's own effective capabilities
 * (GET /role-permissions/mine — item 7's granular RBAC) right after the
 * session resolves, so client-side route guards (ProtectedRoute.tsx's
 * RequireCapability) can show/hide screens for a `publisher` (or an
 * `editor` a manager has granted extra capabilities to) correctly, not
 * just for a hardcoded "manager" role. This is UX convenience only — see
 * requireCapability in the Worker for the real enforcement.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Capability, SessionUser } from "@demandhub-cms/shared";
import { authApi, rolePermissionsApi } from "../lib/api";

interface AuthContextValue {
  user: SessionUser | null;
  loading: boolean;
  /** A manager who has not turned on two-step sign-in yet: the app shows only the screen that turns it on. */
  /** The signed-in user's own effective capabilities. A manager has every
   * capability implicitly (see `can` below) even though this array only
   * ever holds real rows for editor/publisher. */
  capabilities: Capability[];
  /** True if the current user may perform `capability` — always true for
   * a manager, otherwise checks the fetched `capabilities` list. */
  can: (capability: Capability) => boolean;
  /** Checks the password. `needsCode` means the account uses two-step sign-in: call `finishSignIn` after the code is accepted. */
  login: (email: string, password: string) => Promise<{ needsCode: boolean }>;
  /** Loads the new session once sign-in is complete (after the code, for two-step accounts). */
  finishSignIn: () => Promise<void>;
  /** Re-reads the signed-in person (e.g. after turning two-step sign-in on or off). */
  refreshUser: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [loading, setLoading] = useState(true);

  async function loadCapabilities() {
    try {
      const res = await rolePermissionsApi.mine();
      setCapabilities(res.capabilities as Capability[]);
    } catch {
      // Non-fatal: worst case a publisher/editor briefly doesn't see a nav
      // link they actually have access to. The server-side check is what
      // actually matters for security.
      setCapabilities([]);
    }
  }

  useEffect(() => {
    authApi
      .getSession()
      .then(async (sessionUser) => {
        setUser(sessionUser);
        if (sessionUser) return settle(sessionUser);
      })
      .finally(() => setLoading(false));
  }, []);

  async function settle(_sessionUser: SessionUser) {
    await loadCapabilities();
  }

  async function finishSignIn() {
    const sessionUser = await authApi.getSession();
    setUser(sessionUser);
    if (sessionUser) await settle(sessionUser);
  }

  async function login(email: string, password: string) {
    const { needsCode } = await authApi.login(email, password);
    if (!needsCode) await finishSignIn();
    return { needsCode };
  }

  async function refreshUser() {
    const sessionUser = await authApi.getSession();
    setUser(sessionUser);
    if (sessionUser) await settle(sessionUser);
  }

  async function logout() {
    await authApi.logout();
    setUser(null);
    setCapabilities([]);
  }

  function can(capability: Capability): boolean {
    return user?.role === "manager" || capabilities.includes(capability);
  }

  return (
    <AuthContext.Provider value={{ user, loading, capabilities, can, login, finishSignIn, refreshUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
